import { Container, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import WaCard from '../../components/GeneralComponents/WaCard';
import IconDialog from '../../components/IconDialog/IconDialog';
import OtherBankObligations from '../../components/OtherBankObligations/OtherBankObligations';
import NegotiationFlowStepper from '../../components/Stepper/NegotiationFlowStepper';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import useDictionary from '../../dictionaries/context/useDictionary';
import { BankType, BrandType, DebtManager, NegotiationType, OutDebt } from '../../generated/graphql';
import { refreshAndSet, session } from '../../utils/auth';
import { getBankCardInfo } from '../../utils/getBankCardInfo';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { loadingMessage } from '../../utils/loadingMessage';
import { useGetProfileAndDebtsReport } from '../../utils/useGetProfileAndDebtsReport';
import { useLocalAdminInfo } from '../../utils/useLocalAdminInfo';
import { TranquiTheme } from '../App/theme';
import DebtDetailsNegotiationConfirmation from './ConfirmationView/DebtDetailsNegotiationConfirmation';
import DebtDetailsNegOptions from './DebtDetailsNegOptions';
import DebtDetailsNegotiationSummary from './DebtDetailsNegotiationSummary';
import TitleAndSubTitleText from './TitleAndSubtitleText';
import { transformInfoBank } from './transformInfoBank';
import { useDebtDetails } from './useDebtDetails';

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    padding: theme.spacing(2, 3),
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  waCard: {
    transition: 'all .4s',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '& p': {
      textAlign: 'center',
    },
  },
  subtitle: {
    ...theme.typography.capitalize as any,
    color: theme.palette.text5.main,
  },
  title: {
    color: theme.palette.indigo.main,
  },
  disclaimerAlert: {
    marginBottom: theme.spacing(2),
  },
}));

const getLoader = (open: boolean) => (
  <IconDialog icon="loading" rotating open={open}>
    <Typography align="center" variant="h3">{loadingMessage}</Typography>
  </IconDialog>
);

interface DebtDetailsProps { }

const DebtDetails: React.FC<DebtDetailsProps> = () => {
  const classes = useStyles();
  const { gT } = useDictionary();

  const {
    isLoading, outDebt, negOptions, gracePeriod, onChangeGracePeriod,
    profileData, step, outDebts, confirmedOption,
    selectedOption, setSelectedOption, selectedAlternative, setSelectedAlternative,
  } = useDebtDetails();

  const { outDebtsReportData } = useGetProfileAndDebtsReport();
  const [ adminInfo ] = useLocalAdminInfo();

  const [ bank, setBank ] = useBankContext();
  const [ contextBrand, setBrand ] = useBrandContext();

  // navigation
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const cleanPath = path.replace('/:step?', '');
  const cleanUrl = cleanPath.replace(':debtId', outDebt?.id || '');
  const onPreConfirmAlternative = React.useCallback(() => history.push(`${cleanUrl}/resumen`), [ history, cleanUrl ]);
  const onConfirm = React.useCallback(() => history.push(`${cleanUrl}/confirmacion`), [ history, cleanUrl ]);
  const onBackToOptions = () => {
    history.push(cleanUrl);
  };
  const unique = negOptions.length === 1 ? negOptions[0] : null;

  const handleChangeBankObligation = React.useCallback(
    async (brand: BrandType, debtManager: DebtManager, newBank: BankType) => {
      setBrand(brand);
      setBank(newBank);
      await refreshAndSet({ bank: newBank, brand, debtManager });
      history.push('/');
    },
    [ setBrand, setBank, history ],
  );

  React.useEffect(() => {
    if (!outDebt?.negotiation) return;
    setSelectedOption(outDebt.negotiation.negotiationOption || null);
    setSelectedAlternative(outDebt.negotiation.alternative || null);
    onConfirm();
  }, [ confirmedOption, setSelectedOption, setSelectedAlternative,
    onConfirm, url, outDebt?.negotiation ]);

  React.useEffect(() => {
    if (!outDebt) return;

    setBank(transformInfoBank(outDebt).bank);
    setBrand(outDebt?.brand || undefined);
  }, [ outDebt, setBank, setBrand ]);

  const bankCardInfo = React.useMemo(
    () => getBankCardInfo(outDebt as OutDebt, negOptions),
    [ outDebt, negOptions ],
  );
  const otherObligations = outDebtsReportData?.outDebtsReport.managedBanks || [];

  const breadcrumbsItems: any[] = [];

  if (!isLoading && (!profileData?.profile || !outDebt)) return <Redirect to="/portal/deudas" />;
  if (!profileData?.profile || !outDebt) return getLoader(true);

  if (!session.identityValidated) {
    return <Redirect to="/portal/validacion-identidad" />;
  }

  const { negState, isConfirmedByUser, isNanocredit, isApprovedByBank,
    isTotalPayment, opType, isSentToBank, isNoOptions, isPunished,
  } = getNegBooleans(outDebt as OutDebt, selectedOption ?? undefined);

  const finishedSuffix = isApprovedByBank && isTotalPayment ? '_finished' : '';
  const prePaymentSuffix = (!!outDebt.negotiation
    && (!!outDebt.negotiation?.alternative.prePayment || isTotalPayment)
    && (isConfirmedByUser || isSentToBank)) ? '_prePayment' : '';
  const negTypeNanocreditSuffix = (isNanocredit && !isPunished && step === 'confirmation') ? `_${opType}_nanocredit` : '';
  const appTitle = gT(`general_debtsView_title_${step}View_${negState}`, { negType: opType ?? '' });
  const appSubtitle = gT(
    `general_debtsView_subtitle_${step}View_${negState}${finishedSuffix}${prePaymentSuffix}${negTypeNanocreditSuffix}`,
    {
      negType: opType || NegotiationType.Refinance,
      bank: transformInfoBank(outDebt).bank,
      brand: contextBrand || '',
      issuingEntity: outDebt.extra?.issuingEntity || '',
    },
  );
  const showOtherObligations = !adminInfo.token;

  const handleResetOption = () => {
    setSelectedOption(null);
    setSelectedAlternative(null);
  };

  return (
    <>
      {breadcrumbsItems.length ? (
        <Breadcrumbs items={breadcrumbsItems} />
      ) : null}

      <Container className={classes.root}>

        {getLoader(isLoading)}

        {!confirmedOption && <NegotiationFlowStepper />}

        <TitleAndSubTitleText
          className={cn(!step && 'mb-4', step && 'mb-2')}
          title={appTitle}
          subtitle={isNoOptions ? undefined : appSubtitle}
        />

        <Switch>
          <Route
            exact
            path={cleanPath}
            render={() => {
              if (unique?.autoContinue) {
                onPreConfirmAlternative();
                return null;
              }
              return (
                <DebtDetailsNegOptions
                  {...{
                    bankCardInfo,
                    negOptions,
                    gracePeriod,
                    profile: profileData!.profile!,
                    onChangeGracePeriod,
                    onPreConfirmAlternative,
                    selectedOption,
                    setSelectedOption,
                    selectedAlternative,
                    setSelectedAlternative,
                  }}
                  outDebt={outDebt as OutDebt}
                  outDebts={outDebts as OutDebt[]}
                />
              );
            }}
          />

          <Route
            path={`${cleanPath}/resumen`}
            render={() => {
              if (!selectedOption || (selectedOption.type !== NegotiationType.NoOptions
                && !selectedAlternative)) return <Redirect to={cleanUrl} />;
              return (
                <DebtDetailsNegotiationSummary
                  {...{ onBackToOptions, onConfirm }}
                  alternative={selectedAlternative || undefined}
                  negOption={selectedOption}
                  outDebt={outDebt as OutDebt}
                  debts={outDebts as OutDebt[] || []}
                />
              );
            }}
          />

          <Route
            path={`${cleanPath}/confirmacion`}
            render={() => {
              if (!confirmedOption) return <Redirect to={cleanUrl} />;
              return (
                <DebtDetailsNegotiationConfirmation
                  outDebt={outDebt as OutDebt}
                  outDebts={outDebts as OutDebt[]}
                  negOptions={negOptions}
                  onReset={handleResetOption}
                />
              );
            }}
          />
        </Switch>

      </Container>

      {bank && otherObligations.length > 0 && showOtherObligations
        && (
          <OtherBankObligations
            banks={otherObligations}
            onChange={handleChangeBankObligation}
          />
        )}

      <WaCard
        withSeparator
        className={classes.waCard}
        waMessage={outDebt
          ? gT('action_wa_button_message_debt')
          : undefined}
        bank={outDebt.bank}
      />
    </>
  );
};

export default DebtDetails;
